import React, { createContext, useState, useEffect } from 'react';
import { supabase } from '../config/supabase';

// Auth Context Type
export interface AuthContextType {
  user: any | null;
  userType: 'customer' | 'business' | 'admin' | null;
  approvalStatus: 'pending' | 'approved' | 'rejected' | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

// Create Auth Context
export const AuthContext = createContext<AuthContextType>({
  user: null,
  userType: null,
  approvalStatus: null,
  loading: true,
  signOut: async () => {}
});

// Auth Provider Component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [userType, setUserType] = useState<'customer' | 'business' | 'admin' | null>(null);
  const [approvalStatus, setApprovalStatus] = useState<'pending' | 'approved' | 'rejected' | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchApprovalStatus = async (email: string) => {
    try {
      // Match case-insensitively — historical rows may have mixed casing
      const { data } = await supabase
        .from('users')
        .select('approval_status, user_type')
        .ilike('email', email.trim().toLowerCase())
        .limit(1);
      const row = data?.[0];
      if (row) {
        setApprovalStatus(row.approval_status || 'pending');
        if (row.user_type) setUserType(row.user_type);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        const type = session.user.user_metadata?.user_type || 'customer';
        setUserType(type);
        fetchApprovalStatus(session.user.email!);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        const type = session.user.user_metadata?.user_type || 'customer';
        setUserType(type);
        fetchApprovalStatus(session.user.email!);
      } else {
        setUserType(null);
        setApprovalStatus(null);
      }
      setLoading(false);
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setUserType(null);
    setApprovalStatus(null);
  };

  return (
    <AuthContext.Provider value={{ user, userType, approvalStatus, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
